import { IStairConfig, IStepGeometry } from '@stair-craft/common/util';

import { centerToPosition } from '../shared-center-to-position/center-to-position';
import { rayWallIntersection } from '../shared-ray-wall-intersection/ray-wall-intersection';
import { U_WINDER_COUNT, WINDER_ANGLE_DEG } from '../u-shape-calculate-layout/calculate-layout';

interface IWinderBoundaries {
    readonly outerWalls: readonly { readonly axis: 'x' | 'z'; readonly value: number }[];
    readonly innerWalls: readonly { readonly axis: 'x' | 'z'; readonly value: number }[];
    readonly outerCorners: readonly { point: { x: number; z: number }; angle: number }[];
    readonly innerCorners: readonly { point: { x: number; z: number }; angle: number }[];
}

/**
 * Rectangular outer/inner boundary walls and their corner points (ordered by angle),
 * mirrored for a right- vs left-hand turn. Extracted from `generateWinderSteps` to
 * keep its complexity low.
 */
function buildWinderBoundaries(
    config: IStairConfig,
    isRight: boolean,
    arcSign: number,
    pivotX: number,
    outerR: number,
    outerBackZ: number,
    turnStartZ: number
): IWinderBoundaries {
    const sw: number = config.stairWidth;

    const outerWalls: readonly { readonly axis: 'x' | 'z'; readonly value: number }[] = isRight
        ? [
              { axis: 'x', value: 0 },
              { axis: 'z', value: outerBackZ },
              { axis: 'x', value: 2 * sw + config.flightGap },
          ]
        : [
              { axis: 'x', value: sw },
              { axis: 'z', value: outerBackZ },
              { axis: 'x', value: -(sw + config.flightGap) },
          ];

    const innerWalls: readonly { readonly axis: 'x' | 'z'; readonly value: number }[] = isRight
        ? [
              { axis: 'x', value: sw },
              { axis: 'z', value: turnStartZ + config.flightGap / 2 },
              { axis: 'x', value: sw + config.flightGap },
          ]
        : [
              { axis: 'x', value: 0 },
              { axis: 'z', value: turnStartZ + config.flightGap / 2 },
              { axis: 'x', value: -config.flightGap },
          ];

    const outerCorners: readonly { point: { x: number; z: number }; angle: number }[] = isRight
        ? [
              { point: { x: 0, z: outerBackZ }, angle: Math.atan2(outerR, arcSign * (0 - pivotX)) },
              {
                  point: { x: 2 * sw + config.flightGap, z: outerBackZ },
                  angle: Math.atan2(outerR, arcSign * (2 * sw + config.flightGap - pivotX)),
              },
          ]
        : [
              { point: { x: sw, z: outerBackZ }, angle: Math.atan2(outerR, arcSign * (sw - pivotX)) },
              {
                  point: { x: -(sw + config.flightGap), z: outerBackZ },
                  angle: Math.atan2(outerR, arcSign * (-(sw + config.flightGap) - pivotX)),
              },
          ];

    const innerCorners: readonly { point: { x: number; z: number }; angle: number }[] = isRight
        ? [
              { point: { x: sw, z: turnStartZ + config.flightGap / 2 }, angle: Math.atan2(config.flightGap / 2, arcSign * (sw - pivotX)) },
              {
                  point: { x: sw + config.flightGap, z: turnStartZ + config.flightGap / 2 },
                  angle: Math.atan2(config.flightGap / 2, arcSign * (sw + config.flightGap - pivotX)),
              },
          ]
        : [
              { point: { x: 0, z: turnStartZ + config.flightGap / 2 }, angle: Math.atan2(config.flightGap / 2, arcSign * (0 - pivotX)) },
              {
                  point: { x: -config.flightGap, z: turnStartZ + config.flightGap / 2 },
                  angle: Math.atan2(config.flightGap / 2, arcSign * (-config.flightGap - pivotX)),
              },
          ];

    return { outerWalls, innerWalls, outerCorners, innerCorners };
}

export function generateWinderSteps(
    flight1Count: number,
    riserHeight: number,
    turnStartZ: number,
    config: IStairConfig,
    startIndex: number
): IStepGeometry[] {
    const steps: IStepGeometry[] = [];
    const sw: number = config.stairWidth;
    const isRight: boolean = config.turnDirection === 'right';
    const angleRad: number = (WINDER_ANGLE_DEG * Math.PI) / 180;

    // Pivot at center between two flights
    const pivotX: number = isRight ? sw + config.flightGap / 2 : -config.flightGap / 2;
    const pivotZ: number = turnStartZ;
    // Side outer walls sit at sw + flightGap/2 from the pivot, so the back wall
    // is placed at the same radius. That keeps the radial walking depth
    // (outer − inner) equal to stairWidth on every ray, regardless of flightGap.
    const outerR: number = sw + config.flightGap / 2;
    const outerBackZ: number = turnStartZ + outerR;

    const arcSign: number = isRight ? -1 : 1;

    const { outerWalls, innerWalls, outerCorners, innerCorners }: IWinderBoundaries = buildWinderBoundaries(
        config,
        isRight,
        arcSign,
        pivotX,
        outerR,
        outerBackZ,
        turnStartZ
    );

    for (let w: number = 0; w < U_WINDER_COUNT; w++) {
        const startAngle: number = w * angleRad;
        const endAngle: number = (w + 1) * angleRad;
        const midAngle: number = (w + 0.5) * angleRad;
        const winderY: number = (flight1Count + w + 1) * riserHeight;

        const dxStart: number = arcSign * Math.cos(startAngle);
        const dzStart: number = Math.sin(startAngle);
        const dxEnd: number = arcSign * Math.cos(endAngle);
        const dzEnd: number = Math.sin(endAngle);

        const outerBack: { x: number; z: number } = rayWallIntersection(pivotX, pivotZ, dxStart, dzStart, outerWalls);
        const outerFront: { x: number; z: number } = rayWallIntersection(pivotX, pivotZ, dxEnd, dzEnd, outerWalls);
        const innerBack: { x: number; z: number } = rayWallIntersection(pivotX, pivotZ, dxStart, dzStart, innerWalls);
        const innerFront: { x: number; z: number } = rayWallIntersection(pivotX, pivotZ, dxEnd, dzEnd, innerWalls);

        // Collect wall corner midpoints that fall within this step's angle range
        const outerMidPoints: { x: number; z: number }[] = outerCorners
            .filter((c: { point: { x: number; z: number }; angle: number }) => c.angle > startAngle + 1e-6 && c.angle < endAngle - 1e-6)
            .map((c: { point: { x: number; z: number }; angle: number }) => c.point);

        const innerMidPoints: { x: number; z: number }[] = innerCorners
            .filter((c: { point: { x: number; z: number }; angle: number }) => c.angle > startAngle + 1e-6 && c.angle < endAngle - 1e-6)
            .map((c: { point: { x: number; z: number }; angle: number }) => c.point);

        const winderCorners: NonNullable<IStepGeometry['winderCorners']> = {
            innerBack,
            innerFront,
            outerBack,
            outerFront,
            ...(outerMidPoints.length > 0 ? { outerMidPoints } : {}),
            ...(innerMidPoints.length > 0 ? { innerMidPoints } : {}),
        };

        // Compute depths from actual corner positions
        const outerDepth: number = Math.round(Math.sqrt((outerFront.x - outerBack.x) ** 2 + (outerFront.z - outerBack.z) ** 2));
        const innerDepth: number = Math.round(Math.sqrt((innerFront.x - innerBack.x) ** 2 + (innerFront.z - innerBack.z) ** 2));
        const treadW: number = sw;

        // Centroid of the four corners
        const cx: number = (innerBack.x + innerFront.x + outerBack.x + outerFront.x) / 4;
        const cz: number = (innerBack.z + innerFront.z + outerBack.z + outerFront.z) / 4;
        const pos: { x: number; z: number } = centerToPosition(cx, cz, treadW, outerDepth, 0);
        const rotationDeg: number = isRight ? -(midAngle * 180) / Math.PI : (midAngle * 180) / Math.PI;

        steps.push({
            index: startIndex + w,
            position: { x: pos.x, y: winderY, z: pos.z },
            treadWidth: treadW,
            treadDepth: outerDepth,
            treadThickness: config.treadThickness,
            // A winder overhangs the step below like a flight tread does.
            noseLength: config.noseLength,
            rotation: rotationDeg,
            isWinder: true,
            isLanding: false,
            riserHeight,
            innerDepth,
            outerDepth,
            winderCorners,
        });
    }

    return steps;
}
